/**
 * Feb 2026 — Packages Compare
 * ------------------------------------------------------------------
 * Side-by-side comparison of any 2+ packages. Rows are the UNION of
 * every line-name/category across the selected packages; columns are
 * the packages themselves. Numbers = qty × months (or blank when a
 * package doesn't include that line).
 *
 * Compact by default (10.5px cell font, sticky first column) so a
 * BD lead can eyeball what the "Silver" plan includes versus "Gold"
 * without opening 3 tabs.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { GitCompareArrows, Package as PackageIcon, X, Search, Copy, Download, Star, StarOff, Sparkles, Loader2, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const CURRENCY_SYMBOL = { USD: "$", INR: "₹", EUR: "€", GBP: "£", AED: "د.إ" };
const fmtMoney = (v, ccy = "USD") =>
  v == null ? "—" : `${CURRENCY_SYMBOL[ccy] || ""}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// Feb 2026 — Normalise a package line to a comparable key. Matching by
// name+category catches "Instagram Reels" in one package vs another
// even if section/order differs.
function lineKey(ln) {
  const name = (ln?.name || "").trim().toLowerCase();
  const cat = (ln?.category || "").trim().toLowerCase();
  return `${cat}::${name}`;
}

export default function PackagesCompareTab({ wsId }) {
  const [packages, setPackages] = useState([]);
  const [details, setDetails] = useState({});   // {pid: fullPackageDoc}
  const [selected, setSelected] = useState([]);  // ordered ids
  const [q, setQ] = useState("");
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [loading, setLoading] = useState(false);
  // Feb 2026 · AI Recommender state
  const [aiOpen, setAiOpen] = useState(false);
  const [brief, setBrief] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const askAi = async () => {
    if (!brief.trim() || brief.trim().length < 10) {
      toast.error("Add at least a sentence describing the prospect first.");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/packages/ai-recommend`,
        { brief: brief.trim(), picked_package_id: selected[0] || null },
        { timeout: 60000 });
      setAiResult(data);
    } catch (e) { toast.error(apiErrorMessage(e, "AI failed")); }
    finally { setAiBusy(false); }
  };

  useEffect(() => {
    if (!wsId) return;
    setLoading(true);
    api.get(`/workspaces/${wsId}/packages`)
      .then((r) => setPackages(Array.isArray(r.data) ? r.data : (r.data?.rows || [])))
      .catch((e) => toast.error(apiErrorMessage(e, "Couldn't load packages")))
      .finally(() => setLoading(false));
  }, [wsId]);

  // Lazy-fetch full detail for each selected pid.
  useEffect(() => {
    const missing = selected.filter((pid) => !details[pid]);
    if (missing.length === 0) return;
    Promise.all(missing.map((pid) =>
      api.get(`/packages/${pid}`)
        .then((r) => [pid, r.data])
        .catch(() => [pid, null])
    )).then((entries) => {
      setDetails((prev) => {
        const next = { ...prev };
        for (const [pid, doc] of entries) {
          if (doc) next[pid] = doc;
        }
        return next;
      });
    });
  }, [selected, details]);

  const filteredPackages = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return packages;
    return packages.filter((p) =>
      (p.name || "").toLowerCase().includes(needle)
      || (p.brand_name || "").toLowerCase().includes(needle)
      || (p.client_name || "").toLowerCase().includes(needle));
  }, [packages, q]);

  const toggle = (pid) => {
    setSelected((prev) => prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);
  };
  const remove = (pid) => setSelected((prev) => prev.filter((x) => x !== pid));

  const selectedDocs = selected.map((pid) => details[pid]).filter(Boolean);

  // Union of line keys across all selected packages.
  const rows = useMemo(() => {
    const map = new Map();   // key → {name, category, per_pkg: {pid: {...}}}
    for (const pkg of selectedDocs) {
      for (const ln of pkg.lines || []) {
        const k = lineKey(ln);
        if (!map.has(k)) {
          map.set(k, {
            key: k,
            name: ln.name || "—",
            category: ln.category || "",
            per_pkg: {},
          });
        }
        map.get(k).per_pkg[pkg.id] = {
          qty: ln.qty_per_month ?? ln.quantity ?? ln.count ?? null,
          effort_hours: ln.effort_hours || null,
          rate: ln.rate || null,
          frequency: ln.frequency || "monthly",
        };
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      return (a.category || "").localeCompare(b.category || "")
          || (a.name || "").localeCompare(b.name || "");
    });
  }, [selectedDocs]);

  // Feb 2026 — Roll each row up to a category summary so a BD lead can
  // eyeball "how many reels" across every displayed package at once.
  const categoryTotals = useMemo(() => {
    const cats = new Map();  // cat → { per_pkg: {pid: {qty, lines}}, grand: N }
    for (const r of rows) {
      const cat = r.category || "other";
      const bucket = cats.get(cat) || { category: cat, per_pkg: {}, grand: 0, lines: 0 };
      bucket.lines += 1;
      for (const pkg of selectedDocs) {
        const c = r.per_pkg[pkg.id];
        if (!c || c.qty == null) continue;
        const slot = bucket.per_pkg[pkg.id] || { qty: 0, lines: 0 };
        slot.qty += Number(c.qty) || 0;
        slot.lines += 1;
        bucket.per_pkg[pkg.id] = slot;
        bucket.grand += Number(c.qty) || 0;
      }
      cats.set(cat, bucket);
    }
    return Array.from(cats.values());
  }, [rows, selectedDocs]);

  // If "only diff" is on, hide lines that are present with the SAME
  // qty across every selected package.
  const visibleRows = useMemo(() => {
    if (!onlyDiff || selectedDocs.length < 2) return rows;
    return rows.filter((r) => {
      const seen = selectedDocs.map((p) => r.per_pkg[p.id]?.qty ?? null);
      const first = seen[0];
      return seen.some((v) => v !== first);
    });
  }, [rows, onlyDiff, selectedDocs]);

  const totals = useMemo(() => {
    const t = {};
    for (const pkg of selectedDocs) {
      const linesTotal = (pkg.lines || []).length;
      const hours = (pkg.totals?.monthly_hours) ?? (pkg.lines || []).reduce((s, l) => s + (l.effort_hours || 0) * (l.qty_per_month || l.quantity || 0), 0);
      const price = pkg.totals?.net_monthly_price ?? pkg.totals?.monthly_price ?? null;
      t[pkg.id] = { lines: linesTotal, hours, price, ccy: pkg.currency };
    }
    return t;
  }, [selectedDocs]);

  const exportCSV = () => {
    const cols = ["Category", "Line", ...selectedDocs.map((p) => p.name || p.id), "Total"];
    const csvRows = [cols.map(csvCell).join(",")];
    for (const r of visibleRows) {
      const rowGrand = selectedDocs.reduce((s, p) => s + (Number(r.per_pkg[p.id]?.qty) || 0), 0);
      csvRows.push([
        csvCell(r.category),
        csvCell(r.name),
        ...selectedDocs.map((p) => csvCell(r.per_pkg[p.id]?.qty ?? "")),
        csvCell(rowGrand),
      ].join(","));
    }
    // Category summary section.
    csvRows.push("");
    csvRows.push(["CATEGORY TOTALS"].join(","));
    for (const c of categoryTotals) {
      csvRows.push([
        csvCell(c.category),
        csvCell(`${c.lines} line${c.lines === 1 ? "" : "s"}`),
        ...selectedDocs.map((p) => csvCell(c.per_pkg[p.id]?.qty ?? 0)),
        csvCell(c.grand),
      ].join(","));
    }
    csvRows.push("");
    csvRows.push(["", "TOTAL LINES", ...selectedDocs.map((p) => totals[p.id].lines), selectedDocs.reduce((s, p) => s + (totals[p.id]?.lines || 0), 0)].join(","));
    csvRows.push(["", "MONTHLY HOURS", ...selectedDocs.map((p) => totals[p.id].hours ?? ""), selectedDocs.reduce((s, p) => s + (Number(totals[p.id]?.hours) || 0), 0)].join(","));
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `packages-compare-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="pkg-compare-tab">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px] space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
            <GitCompareArrows className="w-3 h-3" /> Pick packages to compare
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search packages…"
                    value={q} onChange={(e) => setQ(e.target.value)}
                    className="pl-8 h-8 rounded-none"
                    data-testid="pkg-compare-search" />
          </div>
          <div className="border border-border max-h-64 overflow-y-auto" data-testid="pkg-compare-picker">
            {loading && <div className="p-3 text-xs text-muted-foreground">Loading…</div>}
            {!loading && filteredPackages.length === 0 && (
              <div className="p-3 text-xs text-muted-foreground italic">No packages match.</div>
            )}
            {filteredPackages.map((p) => {
              const on = selected.includes(p.id);
              return (
                <button key={p.id} type="button" onClick={() => toggle(p.id)}
                        className={`w-full text-left px-2.5 py-1.5 flex items-center gap-2 border-b border-border last:border-b-0 hover:bg-muted/30 ${on ? "bg-primary/5" : ""}`}
                        data-testid={`pkg-compare-toggle-${p.id}`}>
                  {on
                    ? <Star className="w-3 h-3 text-amber-500 fill-amber-500 shrink-0" />
                    : <StarOff className="w-3 h-3 text-muted-foreground shrink-0" />}
                  <span className="text-xs font-medium truncate">{p.name}</span>
                  <span className="text-[10px] text-muted-foreground truncate ml-auto">
                    {p.brand_name || p.client_name || "—"}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="text-[10px] text-muted-foreground">
            {selected.length} selected
          </div>
        </div>

        <div className="flex-[3] min-w-[520px] space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex-1">
              Comparison — {visibleRows.length} line{visibleRows.length === 1 ? "" : "s"} across {selectedDocs.length} package{selectedDocs.length === 1 ? "" : "s"}
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)}
                     disabled={selectedDocs.length < 2} data-testid="pkg-compare-only-diff" />
              Only differences
            </label>
            <Button variant="outline" size="sm" className="rounded-none h-7 text-xs"
                    onClick={() => setAiOpen((v) => !v)}
                    data-testid="pkg-compare-ai-toggle">
              <Sparkles className="w-3 h-3 mr-1" /> Ask AI
            </Button>
            <Button variant="outline" size="sm" className="rounded-none h-7 text-xs"
                    onClick={exportCSV} disabled={selectedDocs.length === 0}
                    data-testid="pkg-compare-export-csv">
              <Download className="w-3 h-3 mr-1" /> CSV
            </Button>
          </div>

          {aiOpen && (
            <div className="border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="pkg-compare-ai-panel">
              <div className="text-[10px] uppercase tracking-widest text-primary font-semibold flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> AI Package Recommender
              </div>
              <p className="text-[11px] text-muted-foreground">
                Paste the prospect brief / discovery notes below. AI picks the best-fit package and, if you already selected one on the left, calls out up-sell lines the brief hints at needing.
              </p>
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder={"e.g. Series-A SaaS launching in Q2, wants LinkedIn + Instagram thought-leadership, 8 posts + 4 reels a month, budget under 5k…"}
                rows={4}
                className="w-full border border-border p-2 rounded-none text-xs resize-none font-mono"
                data-testid="pkg-compare-ai-brief"
              />
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={askAi} disabled={aiBusy || !brief.trim()}
                        className="rounded-none h-8 text-xs"
                        data-testid="pkg-compare-ai-submit">
                  {aiBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  {aiBusy ? "Thinking…" : "Ask AI"}
                </Button>
                {selectedDocs[0] && <span className="text-[10px] text-muted-foreground">Comparing against <strong>{selectedDocs[0].name}</strong></span>}
              </div>
              {aiResult && (
                <div className="border border-border bg-background p-2 space-y-1.5 text-xs" data-testid="pkg-compare-ai-result">
                  {aiResult.recommended_id && (
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Best-fit</span>
                      <div className="font-semibold flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                        {aiResult.recommended_name || aiResult.recommended_id}
                        {!selected.includes(aiResult.recommended_id) && (
                          <button type="button"
                                  onClick={() => setSelected((prev) => [...prev, aiResult.recommended_id])}
                                  className="text-[10px] text-primary hover:underline ml-1"
                                  data-testid="pkg-compare-ai-add-recommended">
                            + add to compare
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="text-[11px] text-muted-foreground italic">{aiResult.reason}</div>
                  {aiResult.upsell_lines?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Up-sell lines the brief hints at</div>
                      <ul className="mt-0.5 space-y-0.5">
                        {aiResult.upsell_lines.map((l, i) => (
                          <li key={i} className="text-[11px] flex items-start gap-1.5">
                            <span className="text-emerald-700 font-mono shrink-0">+{l.qty_per_month || 1}×</span>
                            <span><strong>{l.name}</strong> <span className="text-muted-foreground">· {l.category} · {l.why}</span></span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {aiResult.risk_flags?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-amber-600" /> Qualify these
                      </div>
                      <ul className="mt-0.5 space-y-0.5">
                        {aiResult.risk_flags.map((r, i) => (
                          <li key={i} className="text-[11px] text-amber-800">• {r}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {selectedDocs.length === 0 ? (
            <div className="border-2 border-dashed border-border p-8 text-center text-sm text-muted-foreground italic" data-testid="pkg-compare-empty">
              Pick at least one package on the left to start comparing.
            </div>
          ) : (
            <div className="border border-border overflow-x-auto max-h-[70vh]">
              <table className="min-w-full text-[10.5px] border-separate border-spacing-0">
                <thead className="sticky top-0 z-20">
                  <tr>
                    <th className="sticky left-0 z-30 bg-muted/60 cc-sticky-col border-r border-b border-border px-2 py-1.5 text-left uppercase tracking-widest text-[9px] text-muted-foreground min-w-[180px] max-w-[220px]">
                      Line
                    </th>
                    {selectedDocs.map((p) => (
                      <th key={p.id} className="px-2 py-1.5 bg-muted/60 border-b border-r border-border text-left min-w-[140px] max-w-[180px]">
                        <div className="flex items-start gap-1">
                          <PackageIcon className="w-3 h-3 mt-0.5 text-primary shrink-0" />
                          <Link to={`/app/packages/${p.id}`} target="_blank" rel="noreferrer"
                                className="font-semibold text-[11px] truncate hover:underline block"
                                title={p.name}>
                            {p.name}
                          </Link>
                          <button type="button" onClick={() => remove(p.id)}
                                  className="ml-auto text-muted-foreground hover:text-rose-600 shrink-0"
                                  title="Remove from compare"
                                  data-testid={`pkg-compare-remove-${p.id}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="text-[9px] text-muted-foreground truncate">
                          {[p.client_name, p.brand_name].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </th>
                    ))}
                    <th className="px-2 py-1.5 bg-primary/10 border-b border-r border-border text-center min-w-[80px] font-semibold text-[10px] uppercase tracking-widest text-primary" data-testid="pkg-compare-total-col-header">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 && (
                    <tr>
                      <td colSpan={selectedDocs.length + 2} className="text-center py-6 text-xs text-muted-foreground italic">
                        {onlyDiff ? "No differences between the selected packages." : "No lines to show."}
                      </td>
                    </tr>
                  )}
                  {/* Group rows by category with a summary header per category. */}
                  {(() => {
                    const groups = [];
                    let currentCat = null;
                    const catByName = new Map(categoryTotals.map((c) => [c.category, c]));
                    for (const r of visibleRows) {
                      const cat = r.category || "other";
                      if (cat !== currentCat) {
                        currentCat = cat;
                        const bucket = catByName.get(cat) || { per_pkg: {}, grand: 0, lines: 0 };
                        groups.push(
                          <tr key={`cat-${cat}`} className="bg-muted/40 border-b border-t border-border" data-testid={`pkg-compare-cat-${cat}`}>
                            <td className="sticky left-0 z-10 bg-muted/40 cc-sticky-col border-r border-border px-2 py-1 uppercase tracking-widest text-[9px] font-semibold text-muted-foreground">
                              {cat}
                              <span className="ml-1 font-mono normal-case text-muted-foreground/60">({bucket.lines || 0} line{bucket.lines === 1 ? "" : "s"})</span>
                            </td>
                            {selectedDocs.map((p) => (
                              <td key={p.id} className="border-r border-border px-2 py-1 text-center font-mono text-[11px] font-semibold text-primary/80" data-testid={`pkg-compare-cat-sum-${cat}-${p.id}`}>
                                {bucket.per_pkg[p.id]?.qty ?? 0}×
                              </td>
                            ))}
                            <td className="bg-primary/10 border-r border-border px-2 py-1 text-center font-mono text-[12px] font-bold text-primary" data-testid={`pkg-compare-cat-grand-${cat}`}>
                              {bucket.grand}×
                            </td>
                          </tr>
                        );
                      }
                      const rowGrand = selectedDocs.reduce((s, p) => s + (Number(r.per_pkg[p.id]?.qty) || 0), 0);
                      groups.push(
                        <tr key={r.key} className="hover:bg-muted/20" data-testid={`pkg-compare-row-${r.key}`}>
                          <td className="sticky left-0 z-10 bg-background cc-sticky-col border-r border-b border-border px-2 py-1 truncate max-w-[220px] pl-4">
                            <span className="font-medium truncate" title={r.name}>{r.name}</span>
                          </td>
                          {selectedDocs.map((p) => {
                            const c = r.per_pkg[p.id];
                            if (!c) return (
                              <td key={p.id} className="border-r border-b border-border px-2 py-1 text-center text-muted-foreground/40" data-testid={`pkg-compare-cell-${r.key}-${p.id}`}>—</td>
                            );
                            return (
                              <td key={p.id} className="border-r border-b border-border px-2 py-1 text-center" data-testid={`pkg-compare-cell-${r.key}-${p.id}`}>
                                <div className="font-mono font-semibold text-[11px]">{c.qty ?? "—"}×</div>
                                <div className="text-[8.5px] text-muted-foreground">
                                  {c.effort_hours ? `${c.effort_hours}h` : ""}
                                  {c.effort_hours && c.rate ? " · " : ""}
                                  {c.rate ? fmtMoney(c.rate, p.currency) : ""}
                                </div>
                              </td>
                            );
                          })}
                          <td className="bg-primary/5 border-r border-b border-border px-2 py-1 text-center font-mono text-[11px] font-bold text-primary" data-testid={`pkg-compare-row-total-${r.key}`}>
                            {rowGrand > 0 ? `${rowGrand}×` : "—"}
                          </td>
                        </tr>
                      );
                    }
                    return groups;
                  })()}
                </tbody>
                {/* Sticky totals footer */}
                <tfoot className="sticky bottom-0 z-20">
                  <tr>
                    <td className="sticky left-0 z-30 bg-muted/60 cc-sticky-col border-r border-t border-border px-2 py-1.5 uppercase tracking-widest text-[9px] text-muted-foreground">
                      Total lines
                    </td>
                    {selectedDocs.map((p) => (
                      <td key={p.id} className="bg-muted/60 border-r border-t border-border px-2 py-1.5 text-center font-mono text-[11px] font-semibold" data-testid={`pkg-compare-total-lines-${p.id}`}>
                        {totals[p.id]?.lines ?? 0}
                      </td>
                    ))}
                    <td className="bg-primary/10 border-r border-t border-border px-2 py-1.5 text-center font-mono text-[11px] font-bold text-primary" data-testid="pkg-compare-total-lines-grand">
                      {selectedDocs.reduce((s, p) => s + (totals[p.id]?.lines || 0), 0)}
                    </td>
                  </tr>
                  <tr>
                    <td className="sticky left-0 z-30 bg-muted/60 cc-sticky-col border-r border-t border-border px-2 py-1.5 uppercase tracking-widest text-[9px] text-muted-foreground">
                      Monthly qty (all lines)
                    </td>
                    {selectedDocs.map((p) => {
                      const q = rows.reduce((s, r) => s + (Number(r.per_pkg[p.id]?.qty) || 0), 0);
                      return (
                        <td key={p.id} className="bg-muted/60 border-r border-t border-border px-2 py-1.5 text-center font-mono text-[11px] font-semibold" data-testid={`pkg-compare-total-qty-${p.id}`}>
                          {q}×
                        </td>
                      );
                    })}
                    <td className="bg-primary/10 border-r border-t border-border px-2 py-1.5 text-center font-mono text-[11px] font-bold text-primary" data-testid="pkg-compare-total-qty-grand">
                      {rows.reduce((s, r) => s + selectedDocs.reduce((s2, p) => s2 + (Number(r.per_pkg[p.id]?.qty) || 0), 0), 0)}×
                    </td>
                  </tr>
                  <tr>
                    <td className="sticky left-0 z-30 bg-muted/60 cc-sticky-col border-r border-t border-border px-2 py-1.5 uppercase tracking-widest text-[9px] text-muted-foreground">
                      Monthly hours
                    </td>
                    {selectedDocs.map((p) => (
                      <td key={p.id} className="bg-muted/60 border-r border-t border-border px-2 py-1.5 text-center font-mono text-[11px]" data-testid={`pkg-compare-total-hours-${p.id}`}>
                        {totals[p.id]?.hours ? `${totals[p.id].hours}h` : "—"}
                      </td>
                    ))}
                    <td className="bg-primary/10 border-r border-t border-border px-2 py-1.5 text-center font-mono text-[11px] font-bold text-primary" data-testid="pkg-compare-total-hours-grand">
                      {(() => {
                        const sum = selectedDocs.reduce((s, p) => s + (Number(totals[p.id]?.hours) || 0), 0);
                        return sum ? `${sum}h` : "—";
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <td className="sticky left-0 z-30 bg-muted/60 cc-sticky-col border-r border-t border-border px-2 py-1.5 uppercase tracking-widest text-[9px] text-muted-foreground">
                      Monthly price
                    </td>
                    {selectedDocs.map((p) => (
                      <td key={p.id} className="bg-muted/60 border-r border-t border-border px-2 py-1.5 text-center font-mono text-[11px] font-semibold" data-testid={`pkg-compare-total-price-${p.id}`}>
                        {fmtMoney(totals[p.id]?.price, totals[p.id]?.ccy)}
                      </td>
                    ))}
                    <td className="bg-primary/10 border-r border-t border-border px-2 py-1.5 text-center font-mono text-[11px] font-bold text-primary" data-testid="pkg-compare-total-price-grand">
                      —
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function csvCell(v) {
  if (v == null) return "";
  const s = String(v);
  return /["\n,]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
